// Мини-слот 1x3 над основным полем: три окна со створками, GDD раздел 3.2 зовёт их
// барабанами 4-6.
//
// Створки стоят закрытыми с начала игры. Wild на основном барабане открывает створку
// под своим номером, и открытой она остаётся до конца фичи, см. GDD, раздел 5.1.
//
// Под открытой створкой стоит не символ полосы, а значение множителя: тот же Wild Multiplier,
// что выпадает на поле, только застывший на своём числе. Окно показывает заряд своего
// манометра, см. GDD, раздел 5.2: на стадии 1 барабан открывается с единицей, на стадии 2
// число растёт вместе со стрелкой.
//
// Появляется и меняется число ходом: анимация появления множителя, а за ней его поза.
// Так игрок замечает и открывшееся окно, и выросший множитель.
//
// Настоящий барабан со своей полосой под створкой тоже есть, но крутится он один раз за фичу:
// его крутит бонус Steam Spin, см. GDD, раздел 5.3. На это время множители уходят — игрок
// смотрит на символы, из которых сложится выигрыш. Выигрыш показывается анимациями символов,
// а сумму называет счётчик на закрытом поле.
//
// Кончив бонус, створки закрываются снова: фича возвращается в стадию 1, см. GDD, раздел 5.4.
//
// Координаты здесь те же, что у барабанов: это пиксели картинки барабанов.

import { Container } from 'pixi.js';

import type { GameConfig, SymbolId } from '../../api/gameConfig';
import type { SpinResult } from '../../api/spin';
import { BONUS_COVER_SKELETON, REELS_GRID, SYMBOL_WIN_ANIMATION, markedBlinkAnimation, markedWinAnimation } from '../../assets/catalog';
import type { AssetManager, Spine } from '../../assets/manager';
import type { SymbolLibrary } from '../../game/symbols';
import { BONUS_ROWS, ReelsView, columnX, rowY, type Cell } from '../../ui/reels';
import { animationSeconds, holdSpine, poseSpine } from '../../ui/scene';
import { winningCells } from '../../ui/winShow';

/**
 * Сколько барабаны мини-слота крутятся вхолостую.
 *
 * Выдержка своя, а не общая с основным полем: итог бонуса известен заранее, а игрок должен
 * успеть перевести на мини-слот взгляд. Поэтому она короче выдержки оплаченного спина.
 */
const SPIN_MS: number = 1000;

/** Что делает створка. */
type CoverPhase = 'closed' | 'opening' | 'open' | 'closing';

/** Одно окно мини-слота: створка над барабаном. */
class BonusCover {
  readonly view: Spine;

  #phase: CoverPhase = 'closed';
  #seconds = 0;
  #onFinished: (() => void) | null = null;

  constructor(assets: AssetManager, x: number, y: number) {
    this.view = assets.spine(BONUS_COVER_SKELETON);
    this.view.position.set(x, y);
    holdSpine(this.view, BONUS_COVER_SKELETON.animations.close);
  }

  /** Открыто ли окно. Под открытой створкой видно то, что стоит на барабане. */
  get opened(): boolean {
    return this.#phase === 'open' || this.#phase === 'opening';
  }

  /** Открывает створку и ждёт, пока она уедет. Открытая створка ожидания не держит. */
  open(): Promise<void> {
    return this.#move('closed', 'opening', BONUS_COVER_SKELETON.animations.open);
  }

  /** Закрывает створку и ждёт, пока она приедет. Так кончается бонус, см. GDD, раздел 5.4. */
  close(): Promise<void> {
    return this.#move('open', 'closing', BONUS_COVER_SKELETON.animations.close);
  }

  /** Ставит створку без хода: так игра показывает состояние сессии на загрузке. */
  show(open: boolean): void {
    if (open)
      this.#showOpen();
    else
      this.#showClosed();
  }

  /** Проходит ход створки разом. */
  skip(): void {
    if (this.#phase === 'opening')
      this.#showOpen();
    else if (this.#phase === 'closing')
      this.#showClosed();
  }

  update(seconds: number): void {
    if (this.#phase === 'open' || this.#phase === 'closed')
      return;

    this.#seconds += seconds;

    const animation: string = this.#phase === 'opening' ? BONUS_COVER_SKELETON.animations.open : BONUS_COVER_SKELETON.animations.close;

    if (this.#seconds >= animationSeconds(this.view, animation))
      this.#finish(this.#phase === 'opening' ? 'open' : 'closed');
  }

  /** Пускает ход створки. Створка не в исходном состоянии ожидания не держит. */
  #move(from: CoverPhase, going: CoverPhase, animation: string): Promise<void> {
    if (this.#phase !== from)
      return Promise.resolve();

    this.#phase = going;
    this.#seconds = 0;
    this.view.state.setAnimation(0, animation, false);

    return new Promise<void>((resolve) => {
      this.#onFinished = resolve;
    });
  }

  #showOpen(): void {
    if (this.#phase === 'open')
      return;

    holdSpine(this.view, BONUS_COVER_SKELETON.animations.open);
    this.#finish('open');
  }

  #showClosed(): void {
    if (this.#phase === 'closed')
      return;

    holdSpine(this.view, BONUS_COVER_SKELETON.animations.close);
    this.#finish('closed');
  }

  /** Кончает ход и будит того, кто его ждёт. Второй раз будить некого. */
  #finish(phase: CoverPhase): void {
    this.#phase = phase;
    this.#seconds = 0;

    const finished: (() => void) | null = this.#onFinished;

    this.#onFinished = null;
    finished?.();
  }
}

/** Мини-слот 1x3: множители под створками, а под ними барабаны бонуса. */
export class BonusReels {
  readonly view: Container = new Container();

  readonly #reels: ReelsView;
  readonly #covers: BonusCover[];
  readonly #columns: number;
  readonly #symbols: SymbolLibrary;

  /** Значения множителей в окнах. Их показывает скелет Wild Multiplier, застывший на числе. */
  readonly #plates: Spine[] = [];
  readonly #values: number[];

  /** Стоят ли в окнах множители. Нет — под створками крутятся барабаны бонуса. */
  #multipliers = true;

  /** Анимации выигравших символов. Они лежат поверх барабанов и уходят вместе с показом. */
  readonly #wins: Container = new Container();
  readonly #played: Spine[] = [];

  /** Сколько осталось ждать. Ноль — ожидания нет. */
  #left = 0;
  #onElapsed: ((whole: boolean) => void) | null = null;

  /**
   * @param board Описание доски мини-слота: полосы, ряд и линия.
   * @param multiplier Идентификатор символа Wild Multiplier. Его скелет и рисует значение.
   */
  constructor(board: GameConfig, symbols: SymbolLibrary, assets: AssetManager, multiplier: SymbolId) {
    this.#columns = board.reels.length;
    this.#symbols = symbols;
    this.#values = new Array<number>(this.#columns).fill(0);

    // Барабаны мини-слота живут той же лентой, что и основные: у них своя полоса и свой ряд.
    // Сетка колонок у них общая с полем, отличается только высота ряда.
    this.#reels = new ReelsView(board, symbols, BONUS_ROWS);

    this.#covers = [];

    const multipliers = new Container();

    for (let column = 0; column < this.#columns; column++) {
      const x: number = columnX(column, this.#columns);

      // Скелет символа нарисован вокруг своего начала координат, поэтому он встаёт прямо
      // в середину окна, как и статичная картинка символа на барабане.
      const plate: Spine = symbols.spine(multiplier);
      plate.position.set(x, rowY(0, 1, BONUS_ROWS));
      plate.visible = false;

      this.#plates.push(plate);
      multipliers.addChild(plate);

      this.#covers.push(new BonusCover(assets, x, REELS_GRID.bonus.centerY));
    }

    // Слои идут снизу вверх: барабаны, множители над ними, анимации выигрыша и створки.
    this.view.addChild(this.#reels.view, multipliers, this.#wins, ...this.#covers.map((cover: BonusCover) => cover.view));

    this.#draw();
  }

  /** Сколько у мини-слота окон. */
  get columns(): number {
    return this.#columns;
  }

  /**
   * Закрытые окна слева направо. Пустой список — открыты все.
   *
   * По нему запуск бонуса открывает то, что осталось закрытым: Red Hot Spin приходит
   * с любой стадии, и мини-слот к его мигу открыт не всегда, см. GDD, раздел 6.2.
   */
  get closed(): number[] {
    return this.#covers.flatMap((cover: BonusCover, column: number) => (cover.opened ? [] : [column]));
  }

  /**
   * Ставит створки без хода.
   *
   * Так игра показывает состояние сессии на загрузке: барабан, открытый прошлыми раундами,
   * уже стоит открытым, и открывать его на глазах у игрока нечего.
   */
  show(opened: readonly boolean[]): void {
    this.#covers.forEach((cover: BonusCover, column: number) => {
      cover.show(opened[column] === true);
    });

    this.#draw();
  }

  /**
   * Ставит значения множителей в окнах без хода и возвращает их на место, если их убирал бонус.
   *
   * Значение видно только под открытой створкой: закрытое окно показывать нечем. Так игра
   * показывает готовое положение: состояние сессии на загрузке и сброс после бонуса.
   */
  showMultipliers(values: readonly number[]): void {
    this.#setMultipliers(values, false);
  }

  /**
   * Меняет значения множителей ходом: новое число появляется с анимацией.
   *
   * Так растёт множитель на стадии 2: манометр заряжается, и число в окне сменяется у игрока
   * на глазах. Окно, где значение осталось прежним, хода не играет.
   *
   * Хода никто не ждёт: он идёт поверх и кончается сам. Стрелка манометра к этому мигу уже
   * пришла на своё деление, а держать раунд ради одной анимации незачем.
   */
  playMultipliers(values: readonly number[]): void {
    this.#setMultipliers(values, true);
  }

  /**
   * Убирает множители из окон: под створками сейчас крутятся барабаны бонуса.
   *
   * Так начинается спин бонуса: множители уходят из открытых окон, и на их месте встают
   * символы, из которых сложится выигрыш, см. GDD, раздел 5.3.
   */
  hideMultipliers(): void {
    this.#multipliers = false;
    this.#draw();
  }

  /**
   * Открывает створки заданных барабанов и ждёт, пока они уедут.
   *
   * Створки едут разом: один спин может открыть сразу три, и ждать друг друга им незачем.
   *
   * Множитель под уезжающей створкой появляется ходом: створка поднимается, и число
   * проступает вместе с ней, а не стоит готовым с первого кадра.
   */
  open(columns: readonly number[]): Promise<void> {
    if (columns.length === 0)
      return Promise.resolve();

    const opened: Promise<void>[] = columns.map((column: number) => this.#covers[column]?.open() ?? Promise.resolve());

    this.#draw();

    for (const column of columns)
      this.#playPlate(column);

    return Promise.all(opened).then(() => this.#draw());
  }

  /** Закрывает створки и ждёт, пока они приедут. Так кончается бонус, см. GDD, раздел 5.4. */
  close(): Promise<void> {
    const closed: Promise<void>[] = this.#covers.map((cover: BonusCover) => cover.close());

    return Promise.all(closed).then(() => this.#draw());
  }

  /**
   * Крутит мини-слот и останавливает его на итоге бонуса, см. GDD, раздел 5.3.
   *
   * Множители на это время уходят: барабаны крутят свои символы, и из них сложится выигрыш.
   * Спин один на все три барабана: створки к этому мигу открыты все, иначе стадия 2
   * не включилась бы.
   *
   * Итог известен заранее, но выдержку барабаны идут полную: игрок читает прокрутку теми же
   * глазами, что и на основном поле. Скип обрывает выдержку, и тогда барабаны встают разом,
   * а не доезжают своим ходом.
   */
  async spin(result: SpinResult): Promise<void> {
    this.hideMultipliers();
    this.#reels.start();

    if (!(await this.#wait(SPIN_MS / 1000))) {
      this.#reels.place(result);
      return;
    }

    await this.#reels.stop(result);
  }

  /**
   * Показывает выигрыш мини-слота анимациями его символов.
   *
   * Дорожки линии здесь нет. Линия у мини-слота одна и идёт по всему ряду, показывать её
   * нечем: дорожки нарисованы под высоту основного поля. Сумму называет счётчик на закрытом
   * поле, см. ui/fieldCover.ts.
   *
   * Проигранный спин показывать нечего, и ожидание кончается сразу.
   */
  async showWin(result: SpinResult): Promise<void> {
    const cells: Cell[] = winningCells(result.wins);
    if (cells.length === 0)
      return;

    let seconds = 0;

    for (const cell of cells) {
      // Скелет символа нарисован вокруг своего начала координат, поэтому он встаёт прямо
      // в середину ячейки, как и статичная картинка под ним.
      const symbol: Spine = this.#symbols.spine(result.field[cell.row][cell.reel]);

      symbol.position.set(columnX(cell.reel, this.#columns), rowY(cell.row, 1, BONUS_ROWS));
      symbol.state.setAnimation(0, SYMBOL_WIN_ANIMATION, false);

      seconds = Math.max(seconds, animationSeconds(symbol, SYMBOL_WIN_ANIMATION));

      this.#played.push(symbol);
      this.#wins.addChild(symbol);
    }

    // Статичные символы снимаются с барабанов: две картинки одного символа в одной ячейке
    // разъезжаются, как только анимация двинет символ с места.
    this.#reels.hideSymbols(cells);

    await this.#wait(seconds);

    this.#clearWin();
  }

  /** Проходит показ мини-слота разом: барабаны падают, створки доезжают, анимации снимаются. */
  skip(): void {
    this.#reels.drop();
    this.#clearWin();
    this.#elapse(false);

    for (const cover of this.#covers)
      cover.skip();

    this.#poseMultipliers();
    this.#draw();
  }

  update(seconds: number): void {
    this.#reels.update(seconds);

    for (const cover of this.#covers)
      cover.update(seconds);

    if (this.#left <= 0)
      return;

    this.#left -= seconds;

    if (this.#left <= 0)
      this.#elapse(true);
  }

  /**
   * Ждёт заданное время кадрами и говорит, прошло ли оно целиком.
   *
   * Время считается кадрами, а не часами: показ идёт вместе с анимациями, и они идут теми же
   * кадрами. Ожидание кончает либо само время, либо скип: брошенное ожидание не кончилось бы
   * никогда, и раунд встал бы на нём.
   */
  #wait(seconds: number): Promise<boolean> {
    this.#elapse(false);

    if (seconds <= 0)
      return Promise.resolve(true);

    this.#left = seconds;

    return new Promise<boolean>((resolve) => {
      this.#onElapsed = resolve;
    });
  }

  /**
   * Кончает ожидание и будит того, кто его ждёт. Второй раз будить некого.
   *
   * @param whole Прошло ли ожидание целиком. Скип обрывает его на середине.
   */
  #elapse(whole: boolean): void {
    this.#left = 0;

    const elapsed: ((whole: boolean) => void) | null = this.#onElapsed;

    this.#onElapsed = null;
    elapsed?.(whole);
  }

  /** Снимает анимации выигрыша и возвращает барабанам их символы. */
  #clearWin(): void {
    for (const symbol of this.#played)
      symbol.destroy();

    this.#played.length = 0;
    this.#reels.showSymbols();
  }

  /**
   * Ставит значения множителей в окнах.
   *
   * @param played Показать ли смену числа ходом. Ход играет только окно, где число сменилось.
   */
  #setMultipliers(values: readonly number[], played: boolean): void {
    values.forEach((value: number, column: number) => {
      if (this.#plates[column] === undefined || this.#values[column] === value)
        return;

      this.#values[column] = value;

      if (played)
        this.#playPlate(column);
      else
        this.#posePlate(column);
    });

    this.#multipliers = true;
    this.#draw();
  }

  /** Пускает появление множителя: число проступает и остаётся стоять на своей позе. */
  #playPlate(column: number): void {
    const plate: Spine | undefined = this.#plates[column];
    const value: number = this.#values[column];

    if (plate === undefined || value <= 0)
      return;

    // За появлением ставится сама поза числа: остановленная дорожка держит её первый кадр,
    // и окно остаётся с тем же видом, что и без хода.
    plate.state.setAnimation(0, markedBlinkAnimation(value), false);
    plate.state.addAnimation(0, markedWinAnimation(value), false, 0).timeScale = 0;
  }

  /** Ставит множитель на его позу без хода. */
  #posePlate(column: number): void {
    const plate: Spine | undefined = this.#plates[column];
    const value: number = this.#values[column];

    if (plate === undefined || value <= 0)
      return;

    poseSpine(plate, markedWinAnimation(value));
  }

  /** Ставит все множители на их позы: игрок торопит показ, и досматривать появление нечего. */
  #poseMultipliers(): void {
    for (let column = 0; column < this.#plates.length; column++)
      this.#posePlate(column);
  }

  /**
   * Показывает то, что сейчас стоит в окнах: множители или барабаны бонуса.
   *
   * Барабаны и множители не встречаются: под открытой створкой видно ровно одно из двух.
   * Множитель со значением ноль не показывается: его ещё не ставили.
   */
  #draw(): void {
    this.#reels.view.visible = !this.#multipliers;

    this.#plates.forEach((plate: Spine, column: number) => {
      plate.visible = this.#multipliers && this.#values[column] > 0 && this.#covers[column].opened;
    });
  }
}
